import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import {
  DEFAULT_LOCALE_PROFILE, setActiveLocaleProfile, formatMoney, formatNumber, formatDate, formatDateTime,
  formatShortDate, isValidPhone, hasPaymentGateway,
} from '../utils/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CACHE_KEY = 'institute_locale';
const LocaleContext = createContext(null);

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// Loads the institute's country profile once per login and makes it the default for the
// formatMoney / formatDate helpers, so plain imports from utils/locale follow the country too.
export function LocaleProvider({ children }) {
  const { user, loading } = useAuth();
  const [profile, setProfile] = useState(() => {
    const cached = readCache() || DEFAULT_LOCALE_PROFILE;
    setActiveLocaleProfile(cached);
    return cached;
  });

  useEffect(() => {
    // While the session is being restored user is still null; keep the cached profile until then.
    if (loading) return;
    if (!user) {
      setActiveLocaleProfile(DEFAULT_LOCALE_PROFILE);
      setProfile(DEFAULT_LOCALE_PROFILE);
      try { localStorage.removeItem(CACHE_KEY); } catch { /* storage blocked */ }
      return;
    }
    let cancelled = false;
    axios.get(`${API}/system/locale`)
      .then((res) => {
        if (cancelled || !res.data?.code) return;
        setActiveLocaleProfile(res.data);
        setProfile({ ...DEFAULT_LOCALE_PROFILE, ...res.data });
        try { localStorage.setItem(CACHE_KEY, JSON.stringify(res.data)); } catch { /* storage blocked */ }
      })
      .catch(() => { /* keep the cached or India profile */ });
    return () => { cancelled = true; };
  }, [loading, user?.id, user?.institute_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = useMemo(() => ({
    profile,
    formatMoney: (amount, options) => formatMoney(amount, options, profile),
    formatNumber: (n, options) => formatNumber(n, options, profile),
    formatDate: (d, options) => formatDate(d, options, profile),
    formatDateTime: (d, options) => formatDateTime(d, options, profile),
    formatShortDate: (d) => formatShortDate(d, profile),
    isValidPhone: (phone) => isValidPhone(phone, profile),
    hasPaymentGateway: (gateway) => hasPaymentGateway(gateway, profile),
  }), [profile]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  const ctx = useContext(LocaleContext);
  if (ctx) return ctx;
  // Outside the provider (tests, public pages): India defaults.
  return {
    profile: DEFAULT_LOCALE_PROFILE,
    formatMoney: (amount, options) => formatMoney(amount, options, DEFAULT_LOCALE_PROFILE),
    formatNumber: (n, options) => formatNumber(n, options, DEFAULT_LOCALE_PROFILE),
    formatDate: (d, options) => formatDate(d, options, DEFAULT_LOCALE_PROFILE),
    formatDateTime: (d, options) => formatDateTime(d, options, DEFAULT_LOCALE_PROFILE),
    formatShortDate: (d) => formatShortDate(d, DEFAULT_LOCALE_PROFILE),
    isValidPhone: (phone) => isValidPhone(phone, DEFAULT_LOCALE_PROFILE),
    hasPaymentGateway: (gateway) => hasPaymentGateway(gateway, DEFAULT_LOCALE_PROFILE),
  };
}
